import Link from "next/link";
import { NextRouter, useRouter } from "next/router";
import { useState } from "react";

import { useTranslation } from "../../core/translation.core";
import ChevronLeftIcon from "../../icons/chevronLeft.icon";
import ChevronRightIcon from "../../icons/chevronRight.icon";

export const inputCss =
  "h-7 border my-2 pl-5 rounded-sm " +
  "hover:border-blue-200 hover:border-2 " +
  "focus-visible:outline-none focus-visible:border-blue-600 focus-visible:border-2 focus-visible:shadow-sm focus-visible:shadow-blue-800 " + // focus visible
  "duration-1000 transition-[box-shadow] " +
  "disabled:text-gray-400 " +
  "text-sm text-black font-bold ";
interface PageNavProps {
  page: number;
  size: number;
  total: number;
  pathname: string;
  query?: NextRouter["query"];
}

export default function PageNav({ page, size, total, pathname, query }: PageNavProps) {
  const { t, msg } = useTranslation();
  const { push } = useRouter();
  const [navigatePage, setNavigatePage] = useState<number>(page);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (navigatePage === page) return null;

    push({
      pathname: pathname,
      query: { ...query, page: navigatePage },
    });
  };

  if (!size || !total) return null;
  const pageCount = Math.ceil(total / size);
  const pageArr = Array.from(Array(pageCount).keys()).map((i, idx) => idx + 1);
  const { mobile, desktop } = getStartAndEndIndex(page);

  return (
    <div className="mt-2 space-y-4 py-4 xl:grid grid-cols-[1fr_auto_1fr]">
      <div></div>

      <div className="flex gap-2 justify-center items-center ">
        <NavLink disabled={page === 1} page={page - 1}>
          <ChevronLeftIcon className="w-5 h-5" />
        </NavLink>

        {/* Desktop nav List */}
        <div id="page-nav" className="hidden md:flex gap-2">
          {pageArr.slice(desktop.startIndex, desktop.endIndex).map((i) => (
            <NavLink key={i} isActive={page === i} page={i} pathname={pathname}>
              {i}
            </NavLink>
          ))}
        </div>

        {/* mobile nav List */}
        <div className="flex md:hidden ">
          {pageArr.slice(mobile.startIndex, mobile.endIndex).map((i) => (
            <NavLink key={i} isActive={page === i} page={i} pathname={pathname}>
              {i}
            </NavLink>
          ))}
        </div>

        <NavLink disabled={page >= pageCount} page={page + 1}>
          <ChevronRightIcon className="w-5 h-5" />
        </NavLink>
      </div>

      <form onSubmit={handleSubmit} className="text-base place-self-end flex justify-end items-center">
        <label className="mx-2 ">
          <span className="dark:text-white">{t(msg.product.page)}</span>

          <input
            className={`${inputCss} !w-14 `}
            min={1}
            max={pageCount}
            type="number"
            value={navigatePage}
            onChange={(e) => setNavigatePage(e.target.valueAsNumber)}
          />
        </label>

        <span className="dark:text-white">/ {pageCount}</span>

        <input
          type="submit"
          value={t(msg.product.go)}
          className="bg-primary text-white px-1 ml-2 cursor-pointer hover:bg-white hover:border-white hover:text-black"
        />
      </form>
    </div>
  );
}

interface NavButtonProps {
  children: React.ReactNode;
  isActive?: boolean;
  disabled?: boolean;
  page: number;
  pathname?: string;
  onClick?: () => void;
}

const NavLink = ({ children, isActive, disabled = false, page, pathname }: NavButtonProps) => {
  const { query } = useRouter();

  if (disabled) return null;

  let className = "w-8 h-8 flex justify-center items-center rounded-full border border-gray-300 dark:text-white ";

  if (isActive) {
    className += "bg-her-blue text-white ";
  }

  if (disabled) {
    className += "bg-gray-200 text-white ";
  }

  return (
    <Link
      data-page={children}
      data-selected={isActive}
      className={className}
      href={{
        pathname: pathname,
        query: { ...query, page },
      }}
    >
      {children}
    </Link>
  );
};

const getStartAndEndIndex = (currentPage: number) => {
  // change range size to change the number of btns shown on mobile and desktop respectively
  const rangeSize = [3, 10];
  const mappedRange = rangeSize.map((range) => {
    const remainder = currentPage % range;
    const start = currentPage - remainder;
    const startIndex = remainder === 0 && currentPage >= range ? start - range : start;
    const endIndex = startIndex + range;
    return { endIndex, startIndex };
  });

  const [mobile, desktop] = mappedRange;
  return { desktop, mobile };
};
